import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';

// CONSTANTS
import { siteUrl } from '@/constants/common';
import { ROUTE_MOBILE_APP, ROUTE_PARTNER_MOBILE_APP } from '@/constants/routes';

// UTILITY
import { analyticsPageLoaded } from '@/utility/analytics';
import { getS3ImageUrl } from '@/utility/content';
import { getLink } from '@/utility/routes';

// HOOKS
import useActions from '@/hooks/useActions';

// STORE
import { PagesActions } from '@/store/actions';

// COMPONENTS
import Loader from '@/components/Loader';

// HELP
import Hero from './help/Hero';
import SubscrBenefits from './help/SubscrBenefits';
import ContentBenefits from './help/ContentBenefits';
import DownloadApp from './help/DownloadApp';

// STYLES
import { ContentWrap } from './styles';

const defaultOGImg = getS3ImageUrl('/ogimageapppage.jpeg', '/cp-uploads');

const MobileAppLanding = () => {
  const { isIframe, partner, partnerLoading, contentLoading } = useSelector(
    ({ app, partner: partnerStore, pages }) => ({
      isIframe: app.isIframe,
      partner: partnerStore.data,
      partnerLoading: partnerStore.loadings.partner,
      contentLoading: pages.loading,
    }),
  );

  const [getPagesContent] = useActions([
    pageSlug => PagesActions.getPagesContent.action({ pageSlug }),
  ]);

  useEffect(() => {
    getPagesContent('app-landing');
  }, []);

  const isPartner = !!partner.slug;

  const seoData = {
    canonicalUrl: `${siteUrl}${ROUTE_MOBILE_APP}`,
    ogUrl: isPartner
      ? `${siteUrl}${getLink(ROUTE_PARTNER_MOBILE_APP, { partner: partner.slug })}`
      : `${siteUrl}${ROUTE_MOBILE_APP}`,
    title: 'App',
    description: 'Discover new movies and TV shows and create custom watchlists on our mobile app.',
    image: defaultOGImg,
  };

  return (
    <ContentWrap className="mobile-app">
      {!partnerLoading && (
        <Helmet onChangeClientState={analyticsPageLoaded} titleTemplate="%s | MyBundle">
          <title>{seoData.title}</title>
          <meta property="og:title" content={seoData.title} />
          <meta property="og:site_name" content="MyBundle" />
          <link rel="canonical" href={seoData.canonicalUrl} />
          <meta property="og:url" content={seoData.ogUrl} />
          <meta property="og:description" content={seoData.description} />
          <meta name="description" content={seoData.description} />
          <meta property="og:image" content={seoData.image} />
          <meta name="robots" content="index, follow, max-image-preview:large" />
          <meta name="twitter:title" content={seoData.title} />
          <meta name="twitter:description" content={seoData.description} />
          <meta name="twitter:image" content={seoData.image} />
          <meta name="twitter:site" content="@mybundletv" />
          <meta name="twitter:creator" content="@mybundletv" />
          <meta name="twitter:card" content="summary" />
        </Helmet>
      )}
      <Hero />
      {!isIframe && (
        <>
          <SubscrBenefits />
          <ContentBenefits />
          <DownloadApp />
        </>
      )}
      <Loader active={contentLoading} />
    </ContentWrap>
  );
};

export default React.memo(MobileAppLanding);
